import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { Alert, Button, Checkbox, Modal, useToast } from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { Download } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportar } from '../hooks/useExportacao';

type Props = { open: boolean; onClose: () => void; campanhaId: string };

type Inconsistentes = { quantidade: number; participantes: { id: string; nomeExibicao: string }[] };

export function ExportarModal({ open, onClose, campanhaId }: Props) {
  const { t } = useTranslation(['exportacao', 'campanhas', 'common', 'erros']);
  const { toast } = useToast();
  const titleId = useId();
  const exportar = useExportar(campanhaId);
  const [usoInterno, setUsoInterno] = useState(false);
  const [inconsistentes, setInconsistentes] = useState<Inconsistentes | null>(null);

  useEffect(() => {
    if (open) {
      setUsoInterno(false);
      setInconsistentes(null);
    }
  }, [open]);

  async function baixar() {
    setInconsistentes(null);
    try {
      await exportar.mutateAsync(usoInterno);
      onClose();
      toast.success({ title: t('exportacao:baixado') });
    } catch (error) {
      const chave = mensagemDeErro(error);
      // 422: `quantidade` e `participantes` vêm na raiz do corpo (não em `detalhes`).
      if (chave === 'EXPORTACAO_INCONSISTENTE' && isAxiosError(error)) {
        const d = error.response?.data;
        setInconsistentes({
          quantidade: d?.quantidade ?? 0,
          participantes: Array.isArray(d?.participantes) ? d.participantes : [],
        });
      } else {
        toast.danger({ title: t(`erros:${chave}`) });
      }
    }
  }

  return (
    <Modal open={open} onClose={onClose} ariaLabelledBy={titleId} size="md">
      {/* O mesmo verbo do botão que abre, distinto de "Exportar participantes" (spec 32 R8). */}
      <Modal.Header
        titleId={titleId}
        title={t('campanhas:acoes.baixarPlanilha')}
        onClose={onClose}
      />
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-foreground-muted">{t('exportacao:explicacao')}</p>

          {inconsistentes && (
            <Alert
              variant="danger"
              title={t('erros:EXPORTACAO_INCONSISTENTE', { quantidade: inconsistentes.quantidade })}
            >
              <ul className="list-disc pl-5">
                {inconsistentes.participantes.map((p) => (
                  <li key={p.id}>{p.nomeExibicao}</li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="flex flex-col gap-1">
            <Checkbox
              checked={usoInterno}
              onCheckedChange={setUsoInterno}
              label={t('exportacao:usoInterno')}
              disabled={exportar.isPending}
            />
            <p className="text-xs text-foreground-muted">{t('exportacao:usoInternoHint')}</p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={exportar.isPending}>
          {t('common:cancel')}
        </Button>
        <Button
          variant="primary"
          leftIcon={<Download aria-hidden className="size-4" />}
          onClick={baixar}
          loading={exportar.isPending}
        >
          {t('exportacao:baixar')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
